<script lang="ts">
	import { onMount } from 'svelte';
	//Formating
	import { format } from 'svelte-i18n';
	//Design system components
	import { Button, Select, Slider, Loader, Alert } from '@Shelly/shelly-design-system';
	//Helpers
	import {
		getTemplateService,
		translateTemplateTitle,
	} from 'src/helpers/xt1_helpers/template_helpers';
	import type { TemplateService } from 'src/types/VirtualComponents';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { capFirstChar } from 'src/helpers/utilities';
	//Types
	import type { SelectOptionType } from '@Shelly/shelly-design-system';

	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import type { SchedulesG2Request } from 'src/types/requests/schedules/schedules_g2';

	import { getContext, onDestroy } from 'svelte';
	import type { Writable } from 'svelte/store';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';

	export let device: Device;

	const templateService: TemplateService = getTemplateService(device);

	let actionOnPowerLost = templateService.settings?.on_power_loss || 'none';
	let onPowerLostPosition = templateService.settings?.power_loss_pos || 0;
	let waitForResponse = false;
	let waitForSaveResponse = false;
	let settingsListError = '';
	let options: SelectOptionType[] = null;

	const getOptions = async () => {
		waitForResponse = true;
		const res: SchedulesG2Request = await gen2Request(device, 'Service.ListConfigOptions', {
			id: device.channel ?? 0,
		});
		if (res?.isok) {
			//@ts-ignore
			const data = res?.props || res?.data?.props || [];
			const actionOptions = data.find((i) => i.key == 'on_power_loss')?.options || [];
			options = [];
			actionOptions.forEach((i) => {
				options.push({
					title: translateTemplateTitle(i),
					value: i,
				});
			});
		} else {
			settingsListError = $format('alert_danger_options_list_error', {
				default: 'Could not load the options list.',
			});
		}
		waitForResponse = false;
	};

	const saveValue = async () => {
		waitForSaveResponse = true;
		const config = {
			on_power_loss: actionOnPowerLost,
		};
		if (actionOnPowerLost == 'set_position') {
			config['power_loss_pos'] = onPowerLostPosition;
		}
		let res: SettingRequest = await gen2Request(device, 'Service.SetConfig', {
			id: 0,
			config,
		});
		onSettingsResponse(res, device);
		waitForSaveResponse = false;
	};

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	$: if (stickyBottomAction) {
		if ($showNewLayout && !waitForResponse && !settingsListError && !!options?.length) {
			stickyBottomAction.set({
				onClick: saveValue,
				loading: waitForSaveResponse,
				disabled: waitForSaveResponse,
			});
		} else {
			stickyBottomAction.set(null);
		}
	}

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});

	onMount(getOptions);
	const a11yProps = {
		ariaLabel: translateTemplateTitle(
			$format('slider_title_valve_position', { default: 'valve position' }),
		),
		ariaLabelValue:
			translateTemplateTitle(
				$format('slider_title_valve_position', { default: 'valve position' }),
			) +
			' ' +
			(onPowerLostPosition || 0) +
			' %',
		role: 'slider',
		'aria-valuemin': 0,
		'aria-valuemax': 100,
		'aria-valuenow': onPowerLostPosition || 0,
		'aria-live': 'polite',
	};
</script>

{#if waitForResponse}
	<div class="d-flex justify-content-center">
		<Loader />
	</div>
{:else if settingsListError || !options?.length}
	<Alert type="danger" heading={settingsListError} class="mb-s" />
	<Button
		size="m"
		class="width-full mr-s text-capitalized"
		responsiveSize={{
			sm: 'm',
		}}
		variant="primary"
		on:click={getOptions}
	>
		{$format('button_rescan', { default: 'Rescan' })}
	</Button>
{:else}
	<Select
		id="select-on-power-loss"
		aria-label={$format('select_on_power_loss', {
			default: 'Select on power loss',
		})}
		class="mb-m"
		size="m"
		value={options.find((i) => i['value'] == actionOnPowerLost) || options[0]}
		{options}
		icon="fas fa-list"
		on:change={(e) => {
			actionOnPowerLost = e.detail['value'];
		}}
	/>
	{#if actionOnPowerLost == 'set_position'}
		<Slider
			id="slider-bkh8s0e"
			{a11yProps}
			class="mb-m"
			bind:value={onPowerLostPosition}
			title={capFirstChar($format('slider_title_valve_position', { default: 'valve position' }))}
			step={10}
			type="simple"
		/>
	{/if}
	{#if !$showNewLayout}
		<Button loading={waitForSaveResponse} size="m" width="full" on:click={saveValue}
			>{$format('button_save', { default: 'Save' })}</Button
		>
	{/if}
{/if}
